import { isNumber } from '@art-suite/art-core-ts';
import {
  optional,
  optionalBoolean,
  optionalNumber,
  optionalString,
  string,
  zodLess,
  ZodLessInfer,
  ZodLessInferInput,
  zrecord,
  zunion,
} from '@kaiizen/zod-less';
import { getUniqueId } from '../commonLib';
import { CustomerMetadataSchema, ScanInlineProductSchema, ScanValueTypeSchema } from '../CommonScanSchemas';

export const ScanTemplatePartTypeSchema = zodLess(
  'scanTemplate|groupTemplate|taskTemplate|optionTemplate|lineItemTemplate',
);
export type ScanTemplatePartType = ZodLessInfer<typeof ScanTemplatePartTypeSchema>;

// ************************************************************************
// Scan Template Helpers
// ************************************************************************
/**
 * Returns true if the value is an equation. Right now that means any string that is not unit === "text".
 * If we want to support string values in equations, we'll need to extend the schema somehow.
 * @param value
 * @returns
 */
export const isExpressionValue = (value: ScanTemplateValue): value is { value: string } => value.type !== 'text' && typeof value.value === 'string';
export const isCalculatableValue = (value: ScanTemplateValue): boolean => value.type !== 'text';
export const isCalculatableResult = isNumber;

export const isRequiredValue = (value: ScanTemplateValue): boolean => value.required === true;

// ************************************************************************
// Scan Template Values Schema
// ************************************************************************
export const ScanTemplateValueTypeSchema = zunion(zodLess('text|checkbox|count|usd|sqft|ft|in|acres|fahrenheit|cuyd'), string);
export type ScanTemplateValueType = ZodLessInfer<typeof ScanTemplateValueTypeSchema>;

export const ScanTemplateValuePresenterNames: Record<ScanTemplateValueType, string> = {
  // non-measurement values
  text: 'text',
  checkbox: 'checkbox',

  // measurement values
  count: 'count',
  usd: 'USD',
  sqft: 'square feet',
  cuyd: 'cubic yards',
  ft: 'linear feet',
  in: 'inches',
  acres: 'acres',
  fahrenheit: 'fahrenheit',
};
export const ScanTemplateMeasurementValueTypes = ['count', 'ft', 'sqft', 'cuyd', 'in', 'acres', 'fahrenheit'] as const;

export const ScanTemplateValueMeaningType = zodLess('flooringAreaMeasurement');

export const ScanTemplateValueSchema = zodLess({
  // name is established via the property name in the ScanTemplateValues object
  category: optionalString, // use for custom grouping; matching strings will be grouped together in the UX
  description: optionalString,
  type: ScanTemplateValueTypeSchema.optional(), // anything other than "text" is assumed to be a number or expression string
  meaningType: optional(ScanTemplateValueMeaningType),
  value: ScanValueTypeSchema.optional(),
  minValue: optionalNumber,
  maxValue: optionalNumber,
  required: optionalBoolean,
  // notFieldEditable: optionalBoolean, // BridgeHomes: As an ADMIN, I need to have the ability to enable or block the pricing override function at a work item level.
  valueOptions: zodLess([{ value: ScanValueTypeSchema, label: optionalString }]).optional(),
});
export type ScanTemplateValue = ZodLessInfer<typeof ScanTemplateValueSchema>;

export const ScanTemplateValuesSchema = zrecord(ScanTemplateValueSchema);
export type ScanTemplateValues = ZodLessInfer<typeof ScanTemplateValuesSchema>;

export const ScanTemplateSkuOptionSchema = zunion(string, ScanInlineProductSchema);
export type ScanTemplateSkuOption = ZodLessInfer<typeof ScanTemplateSkuOptionSchema>;

// ************************************************************************
// Scan Template Part Overrides Schema
// ************************************************************************

/**
 * The main data fields of Templates - fields that can be overridden by an override
 */
export const ScanTemplatePartOverridableFieldsSchema = zodLess({
  values: optional(ScanTemplateValuesSchema), // all values in this list must have unique names; null is used in updates to remove values
  hidden: optionalBoolean, // if true, this part will not be shown/added to the Scan by default, but can be added by the user manually when needed
  minimumPhotosRequired: optionalNumber, // if set, must have at least this many photos for this scan-part to be complete
  required: optionalBoolean, // if required, must be complete for parent scan-part to be complete
  skuIdOptions: optional([ScanTemplateSkuOptionSchema]), // list of SKUs to choose from; intended only for line items
  useScopeStageSkus: optionalBoolean, // line-item-level: if true, the user may pick any catalog SKU at the scope stage for this line item (stored on the line item's skuId), not just skuIdOptions
  trade: optionalString, // Normalized trade slug (e.g., "painting", "electrical")
  customerMetadata: optional(CustomerMetadataSchema),
});

export const ScanTemplatePartOverrideSchema = ScanTemplatePartOverridableFieldsSchema.extend({
  id: string, // ScanPartId
  updatedAt: optionalNumber, // unix timestamp in milliseconds
});
export type ScanTemplatePartOverride = ZodLessInfer<typeof ScanTemplatePartOverrideSchema>;

// maps overrideType to override data
export const ScanTemplatePartOverridesSchema = zrecord(ScanTemplatePartOverrideSchema);
export type ScanTemplatePartOverrides = ZodLessInfer<typeof ScanTemplatePartOverridesSchema>;

// ************************************************************************
// Scan Template Part Schema
// ************************************************************************
export const ScanTemplatePartSchema = ScanTemplatePartOverridableFieldsSchema.extend({
  id: string, // ScanPartId
  updatedAt: optionalNumber, // unix timestamp in milliseconds
  createdAt: optionalNumber, // unix timestamp in milliseconds
  name: string, // human readable name for this template part
  description: optionalString, // description for this template part
  category: optionalString, // arbitrary string used for custom grouping; matching strings will be grouped together in the UX
  subtype: optionalString, // arbitrary stream for signaling custom logic:
  // group subtypes: 'otherBedroom', 'otherBathroom' - so we can auto-init the right number of these rooms
  // task subtypes: `appliance` - to signal that this task is for an appliance and we should auto-extract appliance data from data-plate photos

  // OVERRIDES
  // List any number of uniquely-identified overrides. Can be used for market-specific overrides
  // Any specific Scan instance can have one or more overrides enabled.
  // They will be merged together starting with the non-override data and then the overrides.
  // The last-listed override will have top priority.
  overrides: ScanTemplatePartOverridesSchema.optional(), // map of market ID to overrides
});
export type ScanTemplatePart = ZodLessInfer<typeof ScanTemplatePartSchema>;

// ************************************************************************
// Scan Template Schema
// ************************************************************************
export const ScanTemplateLineItemSchema = ScanTemplatePartSchema.meta({
  id: 'ScanTemplateLineItem',
  description: 'ScanTemplateLineItem is the 5th and lowest level of the scan template data hierarchy. It has no children.',
});

export const ScanTemplateOptionSchema = ScanTemplatePartSchema.extend({ lineItemTemplates: optional([ScanTemplateLineItemSchema]) }).meta({
  id: 'ScanTemplateOption',
  description: 'ScanTemplateOption is the 4th level of the scan template data hierarchy. Its children are ScanTemplateLineItems.',
});
export const ScanTemplateTaskSchema = ScanTemplatePartSchema.extend({
  optionTemplates: optional([ScanTemplateOptionSchema]),
}).meta({
  id: 'ScanTemplateTask',
  description: 'ScanTemplateTask is the 3rd level of the scan template data hierarchy. Its children are ScanTemplateOptions.',
});
export const ScanTemplateGroupSchema = ScanTemplatePartSchema.extend({
  /**
   * @deprecated use taskTemplateIds instead
   */
  taskTemplates: optional([ScanTemplateTaskSchema]).meta({ deprecated: true, description: 'use taskTemplateIds instead' }), // Denormalized data: inline task templates
  taskTemplateIds: optional([string]), // Normalized data: task template IDs to reference from the template-wide taskTemplatesById map
}).meta({
  id: 'ScanTemplateGroup',
  description: 'ScanTemplateGroup is the 2nd level of the scan template data hierarchy. Its children are ScanTemplateTasks.',
});
export const ScanTemplateRootSchema = ScanTemplatePartSchema.extend({
  groupTemplates: optional([ScanTemplateGroupSchema]),
  taskTemplatesById: zrecord(ScanTemplateTaskSchema).optional(), // Normalized data: template-wide map of task template ID to task template allowing tasks to be shared across groups
}).meta({
  id: 'ScanTemplateRoot',
  description:
    'The root of the scan template data. This is the main object that contains all the data for a scan template. Its children are ScanTemplateGroups.',
});

/**
 * @deprecated use ScanTemplateRootSchema instead
 */
export const ScanTemplateDataSchema = ScanTemplateRootSchema;

// **************************************************************
// Scan Template Types
// **************************************************************
export type ScanTemplateLineItem = ZodLessInfer<typeof ScanTemplateLineItemSchema>;
export type ScanTemplateOption = ZodLessInfer<typeof ScanTemplateOptionSchema>;
export type ScanTemplateTask = ZodLessInfer<typeof ScanTemplateTaskSchema>;
export type ScanTemplateGroup = Omit<ZodLessInfer<typeof ScanTemplateGroupSchema>, 'taskTemplates'> & {
  taskTemplates?: ZodLessInfer<typeof ScanTemplateTaskSchema>[];
};
export type ScanTemplateRoot = ZodLessInfer<typeof ScanTemplateRootSchema>;

/**
 * @deprecated use ScanTemplateRoot instead
 */
export type ScanTemplateData = ScanTemplateRoot;

// **************************************************************
// Scan Template Input Types
// **************************************************************
// Input types are slightly less restrictive because defaults can be applied for missing fields
// These are helpful when defining inline template data in TypeScript (like when vibe-converting templates)
export type ScanTemplateLineItemInput = ZodLessInferInput<typeof ScanTemplateLineItemSchema>;
export type ScanTemplateOptionInput = ZodLessInferInput<typeof ScanTemplateOptionSchema>;
export type ScanTemplateTaskInput = ZodLessInferInput<typeof ScanTemplateTaskSchema>;
export type ScanTemplateGroupInput = ZodLessInferInput<typeof ScanTemplateGroupSchema>;
export type ScanTemplateRootInput = ZodLessInferInput<typeof ScanTemplateRootSchema>;

// **************************************************************
// Minimum Valid Scan Template Data - use when starting a new template
// **************************************************************

/**
 * @deprecated use initScanTemplateData instead
 */
export const minimumValidScanTemplateData: ScanTemplateRoot = {
  id: getUniqueId(),
  name: 'Blank Template',
};

/**
 * @deprecated use initScanTemplateData instead
 */
export const minimumValidScanTemplateJsonString: string = JSON.stringify(minimumValidScanTemplateData);

export const initScanTemplateData = (name: string): ScanTemplateRoot => ({
  id: getUniqueId(),
  name,
  groupTemplates: [{ id: getUniqueId(), name: 'Bedroom', subtype: 'otherBedroom' }, { id: getUniqueId(), name: 'Bathroom', subtype: 'otherBathroom' }],
});

// **************************************************************
// Scan TEMPLATE UPDATE TYPES
// **************************************************************
export type ScanTemplateValuesUpdates = Record<string, ScanTemplateValue | null>;
export type ScanValueUpdates = { values?: ScanTemplateValuesUpdates };
export type UpdateScanTemplate = Partial<Omit<ScanTemplateRoot, 'values'>> & ScanValueUpdates;
export type UpdateScanTemplateGroup = Partial<Omit<ScanTemplateGroup, 'values'>> & ScanValueUpdates;
export type UpdateScanTemplateTask = Partial<Omit<ScanTemplateTask, 'values'>> & ScanValueUpdates;
export type UpdateScanTemplateResolutionOption = Partial<Omit<ScanTemplateOption, 'values'>> & ScanValueUpdates;
export type UpdateScanTemplateLineItem = Partial<Omit<ScanTemplateLineItem, 'values'>> & ScanValueUpdates;
export type UpdateScanTemplatePart = Partial<Omit<ScanTemplatePart, 'values'>> & ScanValueUpdates;

export type ScanTemplatePartContext = {
  groupId?: string;
  taskId?: string;
  optionId?: string;
  lineItemId?: string;
};

export type ScanTemplatePartWithContext = ScanTemplatePartContext & {
  scanTemplatePart: ScanTemplatePart;
};

export type ScanTemplatePartWithExtendedContext = ScanTemplatePartWithContext & {
  type: ScanTemplatePartType;
};

export type UpdateScanTemplatePartWithContext = ScanTemplatePartContext & {
  updates: UpdateScanTemplatePart;
};
